import { Button, type ButtonProps } from '@/components/ui/button'
import ExternalLinkButton from '@/components/ui/ExternalLinkButton'
import type {
  ExternalLinkButtonSize,
  ExternalLinkButtonColor,
} from '@/components/ui/ExternalLinkButton'
import { cn } from '@/utilities/ui'
import { getDocUrlPath } from '@/utilities/getDocUrlPath'
import Link from 'next/link'
import React from 'react'
import { ArrowUpRight, ArrowDownRight } from 'lucide-react'

import type { Page, Project, Product, Job, File as FileDoc } from '@/payload-types'

type CMSLinkType = {
  appearance?: 'inline' | 'external' | 'splitExternal' | 'splitInternal' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'projects' | 'products' | 'jobs'
    value: Page | Project | Product | Job | string | number
  } | null
  size?: ButtonProps['size'] | ExternalLinkButtonSize | null
  type?: 'custom' | 'reference' | 'file' | 'phone' | 'email' | null
  url?: string | null
  phone?: string | null
  email?: string | null
  file?: FileDoc | string | number | null
  anchor?: string | null
  // Optional props for external appearance
  externalColor?: ExternalLinkButtonColor
  // Optional props for split appearances
  splitTextColor?: string
  splitIconBgColor?: string
  splitIconColor?: string
}

// Helper function to normalize anchor (trim whitespace, remove leading #)
const normalizeAnchor = (anchor: string | null | undefined): string | null => {
  if (!anchor) return null
  const trimmed = anchor.trim()
  if (!trimmed) return null
  // Remove leading # if user added it
  return trimmed.startsWith('#') ? trimmed.slice(1) : trimmed
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    phone,
    email,
    file,
    anchor,
    externalColor = 'brand',
    splitTextColor = 'text-brand',
    splitIconBgColor = 'bg-brand',
    splitIconColor = 'text-white',
  } = props

  // Normalize anchor
  const normalizedAnchor = normalizeAnchor(anchor)

  // Build base href based on link type
  let baseHref: string | null = null

  if (type === 'phone' && phone) {
    baseHref = `tel:${phone.replace(/\s/g, '')}`
  } else if (type === 'email' && email) {
    baseHref = `mailto:${email}`
  } else if (type === 'file' && file) {
    if (typeof file === 'object' && 'url' in file && file.url) {
      baseHref = String(file.url)
    }
  } else if (type === 'reference' && reference) {
    if (
      typeof reference.value === 'object' &&
      reference.value !== null &&
      'slug' in reference.value
    ) {
      const relationTo = reference.relationTo
      if (relationTo === 'pages') {
        baseHref = getDocUrlPath(reference.value as Page)
      } else if (relationTo === 'projects') {
        baseHref = `/projekte/${reference.value.slug}`
      } else if (relationTo === 'products') {
        // Check if it's an external product
        if (
          'type' in reference.value &&
          reference.value.type === 'external' &&
          'externalUrl' in reference.value &&
          reference.value.externalUrl
        ) {
          baseHref = String(reference.value.externalUrl)
        } else {
          baseHref = `/produkte/${reference.value.slug}`
        }
      } else if (relationTo === 'jobs') {
        baseHref = `/karriere/jobs/${reference.value.slug}`
      } else {
        baseHref = `/${relationTo}/${reference.value.slug}`
      }
    }
  } else if (type === 'custom' && url) {
    baseHref = url
  }

  // Append anchor if present (only for reference and custom types)
  const href =
    baseHref && normalizedAnchor && (type === 'reference' || type === 'custom')
      ? `${baseHref}#${normalizedAnchor}`
      : baseHref

  if (!href) return null

  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}
  const linkContent = label || children

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link className={cn('cursor-pointer', className)} href={href} {...newTabProps}>
        {linkContent}
      </Link>
    )
  }

  /* External link button appearance */
  if (appearance === 'external') {
    // Map Button sizes to ExternalLinkButton sizes
    const externalSize: ExternalLinkButtonSize = sizeFromProps === 'lg' ? 'lg' : 'md'

    return (
      <ExternalLinkButton
        href={href}
        size={externalSize}
        color={externalColor}
        external={newTab || false}
        className={className}
      >
        {linkContent}
      </ExternalLinkButton>
    )
  }

  /* Split link appearances — single anchor wrapping text + decorative icon.
   *
   * Was: two sibling <Link>s with the same href, which caused text-only
   * crawlers/SEO audit tools to surface the raw URL as link text for the
   * icon-only anchor, and split the anchor-text signal in two. The icon
   * <span> is aria-hidden so screen readers only announce the text once. */
  if (appearance === 'splitExternal' || appearance === 'splitInternal') {
    const sizeClasses = {
      sm: {
        text: 'text-sm md:text-base',
        icon: 'w-10 h-10 md:w-12 md:h-12',
        iconSize: 'w-4 h-4 md:w-5 md:h-5',
        gap: 'gap-3 md:gap-4',
      },
      md: {
        text: 'text-base md:text-lg',
        icon: 'w-12 h-12',
        iconSize: 'w-5 h-5 md:w-6 md:h-6',
        gap: 'gap-4',
      },
      lg: {
        text: 'text-lg md:text-xl',
        icon: 'w-12 h-12 md:w-14 md:h-14',
        iconSize: 'w-6 h-6 md:w-7 md:h-7',
        gap: 'gap-4 md:gap-6',
      },
    }

    const size = sizeFromProps === 'lg' ? 'lg' : sizeFromProps === 'sm' ? 'sm' : 'md'
    const sizeConfig = sizeClasses[size]
    const isExternal = appearance === 'splitExternal'
    const ArrowIcon = isExternal ? ArrowUpRight : ArrowDownRight

    return (
      <Link
        href={href}
        className={cn('group inline-flex items-center', sizeConfig.gap, className)}
        {...newTabProps}
      >
        <span
          className={cn(
            'font-ui font-medium uppercase transition-opacity duration-200',
            sizeConfig.text,
            splitTextColor,
            'group-hover:opacity-80',
          )}
        >
          {linkContent}
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center shrink-0',
            sizeConfig.icon,
            splitIconBgColor,
            splitIconColor,
          )}
        >
          <ArrowIcon
            className={cn(
              sizeConfig.iconSize,
              'transition-transform duration-300',
              isExternal ? 'group-hover:rotate-45' : 'group-hover:-rotate-45',
            )}
            strokeWidth={2}
          />
        </span>
      </Link>
    )
  }

  /* Standard button appearances */
  const size = appearance === 'link' ? undefined : sizeFromProps || 'md'
  return (
    <Link href={href} {...newTabProps}>
      <Button className={className} size={size} variant={appearance}>
        {linkContent}
      </Button>
    </Link>
  )
}

/**
 * Helper function to convert PayloadCMS link data to CMSLink props
 * This allows easy conversion from PayloadCMS link objects to CMSLink component props
 */
export type PayloadLinkData = {
  type?: ('reference' | 'custom' | 'file' | 'phone' | 'email') | null
  reference?:
    | ({
        relationTo: 'pages'
        value: string | any
      } | null)
    | ({
        relationTo: 'projects'
        value: string | any
      } | null)
    | ({
        relationTo: 'products'
        value: string | any
      } | null)
    | ({
        relationTo: 'jobs'
        value: string | any
      } | null)
  file?: string | any | null
  url?: string | null
  phone?: string | null
  email?: string | null
  label?: string | null
  openInNewTab?: boolean | null
  anchor?: string | null
}

export const convertLinkToCMSLinkProps = (
  link: PayloadLinkData | null | undefined,
): CMSLinkType | null => {
  if (!link || !link.type) return null

  return {
    type: link.type,
    label: link.label,
    newTab: link.openInNewTab,
    anchor: link.anchor,
    // Type-specific fields
    reference: link.reference || null,
    url: link.url || null,
    phone: link.phone || null,
    email: link.email || null,
    file: link.file || null,
  }
}

/**
 * Utility function to extract link data from a PayloadCMS group field that contains
 * an 'enabled' checkbox along with link fields.
 *
 * **Note:** This is primarily for internal use by link components (e.g., LinkWithIcon, CMSLink).
 * Most components should pass full link objects to these components rather than extracting data manually.
 *
 * @param linkGroup - The link group object (may contain 'enabled' and other fields)
 * @returns The link data without the 'enabled' field, suitable for getLinkHref
 * @internal - Primarily used by link components internally
 */
export const extractLinkData = (
  linkGroup: (PayloadLinkData & { enabled?: boolean | null }) | null | undefined,
): PayloadLinkData | null => {
  if (!linkGroup) return null

  // Extract only the link fields, excluding 'enabled'
  const { enabled, ...linkData } = linkGroup
  return linkData as PayloadLinkData
}

/**
 * Helper function to extract href from PayloadCMS link data.
 *
 * **Note:** This is primarily for internal use by link components (e.g., LinkWithIcon, CMSLink).
 * Most components should use LinkWithIcon or CMSLink with link objects rather than extracting hrefs manually.
 * This function is still available for edge cases where a simple href string is needed (e.g., ProductCard).
 *
 * @param link - The PayloadCMS link data object
 * @returns The resolved href string, or null if the link is invalid
 * @internal - Primarily used by link components internally, but available for edge cases
 */
export const getLinkHref = (link: PayloadLinkData | null | undefined): string | null => {
  if (!link || !link.type) return null

  const normalizedAnchor = normalizeAnchor(link.anchor)
  let baseHref: string | null = null

  if (link.type === 'phone' && link.phone) {
    baseHref = `tel:${link.phone.replace(/\s/g, '')}`
  } else if (link.type === 'email' && link.email) {
    baseHref = `mailto:${link.email}`
  } else if (link.type === 'file' && link.file) {
    // Upload field — populated value (depth >= 1) carries a public download URL.
    if (typeof link.file === 'object' && 'url' in link.file && link.file.url) {
      baseHref = String(link.file.url)
    }
  } else if (link.type === 'reference' && link.reference) {
    const ref = link.reference
    if (typeof ref === 'object' && ref !== null && 'value' in ref) {
      const value = ref.value
      if (typeof value === 'object' && value !== null && 'slug' in value) {
        const relationTo = ref.relationTo
        if (relationTo === 'pages') {
          baseHref = getDocUrlPath(value as Page)
        } else if (relationTo === 'projects') {
          baseHref = `/projekte/${value.slug}`
        } else if (relationTo === 'products') {
          // Check if it's an external product
          if (
            'type' in value &&
            value.type === 'external' &&
            'externalUrl' in value &&
            value.externalUrl
          ) {
            baseHref = String(value.externalUrl)
          } else {
            baseHref = `/produkte/${value.slug}`
          }
        } else if (relationTo === 'jobs') {
          baseHref = `/karriere/jobs/${value.slug}`
        } else {
          baseHref = `/${relationTo}/${value.slug}`
        }
      } else if (typeof value === 'string') {
        // Handle string IDs - construct URL based on relationTo
        const relationTo = ref.relationTo
        if (relationTo === 'pages') {
          baseHref = value === 'home' ? '/' : `/${value}`
        } else if (relationTo === 'projects') {
          baseHref = `/projekte/${value}`
        } else if (relationTo === 'products') {
          baseHref = `/produkte/${value}`
        } else if (relationTo === 'jobs') {
          baseHref = `/karriere/jobs/${value}`
        } else {
          baseHref = `/${relationTo}/${value}`
        }
      }
    }
  } else if (link.type === 'custom' && link.url) {
    baseHref = link.url
  }

  // Append anchor if present (only for reference and custom types)
  if (baseHref && normalizedAnchor && (link.type === 'reference' || link.type === 'custom')) {
    return `${baseHref}#${normalizedAnchor}`
  }

  return baseHref
}
